/* import __COLOCATED_TEMPLATE__ from './ai-score-panel.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { later } from '@ember/runloop';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import { put } from 'embercom/lib/ajax';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import {
  ReviewStatus,
  type EntityScoreData,
  type EntityScoreIssue,
  type RuleResult,
} from 'embercom/components/content-readiness/types';

const FADE_OUT_DURATION_MS = 300;

interface Signature {
  Args: {
    scoreData: EntityScoreData;
    onIssueClick?: (issue: EntityScoreIssue) => void;
    onAcceptIssueFix?: (issue: EntityScoreIssue) => void;
    onIssueReviewed?: (issueId: number) => void;
  };
}

const MAX_VISIBLE_ITEMS = 3;

export default class AiScorePanel extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare notificationsService: { notifyError: (message: string) => void };

  @tracked showAllRules = false;
  @tracked showAllIssues = false;
  @tracked reviewedIssueIds = new Set<number>();
  @tracked fadingOutIssueId: number | null = null;

  get ruleResultEntries(): Array<RuleResult & { key: string }> {
    let ruleResults = this.args.scoreData?.rule_results;
    if (!ruleResults) {
      return [];
    }
    return Object.entries(ruleResults).map(([key, result]) => ({
      key,
      ...result,
    }));
  }

  get visibleRuleResultEntries(): Array<RuleResult & { key: string }> {
    if (this.showAllRules) {
      return this.ruleResultEntries;
    }
    return this.ruleResultEntries.slice(0, MAX_VISIBLE_ITEMS);
  }

  get hasMoreRules(): boolean {
    return this.ruleResultEntries.length > MAX_VISIBLE_ITEMS;
  }

  get allIssues(): EntityScoreIssue[] {
    return this.ruleResultEntries
      .flatMap((entry) =>
        entry.issues.map((issue) => ({
          ...issue,
          rule_key: entry.key,
        })),
      )
      .filter((issue) => !this.reviewedIssueIds.has(issue.id));
  }

  get visibleIssues(): EntityScoreIssue[] {
    if (this.showAllIssues) {
      return this.allIssues;
    }
    return this.allIssues.slice(0, MAX_VISIBLE_ITEMS);
  }

  get hasMoreIssues(): boolean {
    return this.allIssues.length > MAX_VISIBLE_ITEMS;
  }

  get totalIssueCount(): number {
    return this.allIssues.length;
  }

  get progressBarColorClass(): string {
    let priority = this.args.scoreData?.priority;
    if (priority === 'P0') {
      return 'bg-vis-salmon-50';
    }
    if (priority === 'P1') {
      return 'bg-vis-yellow-50';
    }
    if (priority === 'P2') {
      return 'bg-vis-slateblue-50';
    }
    if (priority === 'P3') {
      return 'bg-vis-blue-50';
    }
    return 'bg-vis-green-50';
  }

  get priorityBadgeClass(): string {
    let priority = this.args.scoreData?.priority;
    if (priority === 'P0') {
      return 'bg-vis-salmon-50 text-on-error';
    }
    if (priority === 'P1') {
      return 'bg-vis-yellow-50 text-on-notice';
    }
    if (priority === 'P2') {
      return 'bg-vis-slateblue-50 text-on-info';
    }
    if (priority === 'P3') {
      return 'bg-vis-blue-50 text-on-info';
    }
    return 'bg-vis-green-50 text-on-success';
  }

  get priorityLabel(): string {
    let priority = this.args.scoreData?.priority;
    if (priority === 'P0') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.critical',
      );
    }
    if (priority === 'P1') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.poor',
      );
    }
    if (priority === 'P2') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.needs-work',
      );
    }
    if (priority === 'P3') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.good',
      );
    }
    if (priority === 'P4') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.priority.excellent',
      );
    }
    return '';
  }

  statusColorClass = (status: string): string => {
    if (status === 'pass') {
      return 'text-success';
    }
    if (status === 'warn') {
      return 'text-notice';
    }
    return 'text-error';
  };

  statusLabel = (status: string): string => {
    if (status === 'pass') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.status.pass',
      );
    }
    if (status === 'warn') {
      return this.intl.t(
        'knowledge-hub.content-editor.content-readiness.ai-score-panel.status.warn',
      );
    }
    return this.intl.t('knowledge-hub.content-editor.content-readiness.ai-score-panel.status.fail');
  };

  @action
  handleIssueClick(issue: EntityScoreIssue) {
    if (issue.fix_target === 'title' || issue.block_index !== null) {
      this.args.onIssueClick?.(issue);
    }
  }

  @action
  toggleShowAllRules() {
    this.showAllRules = !this.showAllRules;
  }

  @action
  toggleShowAllIssues() {
    this.showAllIssues = !this.showAllIssues;
  }

  @action
  async acceptIssue(issue: EntityScoreIssue) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e: unknown) {
      // permissionsService displays the error modal
      return;
    }
    if (issue.suggested_fix_blocks?.length || issue.fix_target === 'title') {
      this.args.onAcceptIssueFix?.(issue);
    }
    this.markIssueReviewedLocally(issue);
  }

  @action
  async rejectIssue(issue: EntityScoreIssue) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e: unknown) {
      // permissionsService displays the error modal
      return;
    }
    await this.reviewIssue(issue, ReviewStatus.Rejected);
  }

  @action
  async markNotRelevant(issue: EntityScoreIssue) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e: unknown) {
      // permissionsService displays the error modal
      return;
    }
    await this.reviewIssue(issue, ReviewStatus.NotRelevant);
  }

  private markIssueReviewedLocally(issue: EntityScoreIssue) {
    this.fadingOutIssueId = issue.id;
    later(
      this,
      () => {
        this.reviewedIssueIds = new Set([...this.reviewedIssueIds, issue.id]);
        this.fadingOutIssueId = null;
        this.args.onIssueReviewed?.(issue.id);
      },
      FADE_OUT_DURATION_MS,
    );
  }

  private async reviewIssue(issue: EntityScoreIssue, reviewStatus: ReviewStatus) {
    try {
      await put('/teammate_app/content_readiness/review_issue', {
        app_id: this.appService.app.id,
        issue_id: issue.id,
        review_status: reviewStatus,
      });
      this.fadingOutIssueId = issue.id;
      later(
        this,
        () => {
          this.reviewedIssueIds = new Set([...this.reviewedIssueIds, issue.id]);
          this.fadingOutIssueId = null;
          this.args.onIssueReviewed?.(issue.id);
        },
        FADE_OUT_DURATION_MS,
      );
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.content-readiness.ai-score-panel.review-error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'ContentReadiness::AiScorePanel': typeof AiScorePanel;
    'content-readiness/ai-score-panel': typeof AiScorePanel;
  }
}
